import styles from "./CurrentEmployees.module.css";
import { CURRENT_EMPLOYEES_DRAWER_FORM_TITLE } from "pages/CensusAndCurrentInformation/utils/constants";
import { useEffect, useState } from "react";
import { useCensusAndCurrentInformationContext } from "pages/CensusAndCurrentInformation/CensusAndCurrentInformation";
import {
  trackFormError,
  trackPageViewUserStep,
  trackSaveAndContinueUserStep,
} from "utils/analytics/trackEvent";
import { Modal } from "@justworkshr/milo-components";

import CensusDrawer from "uicomponents/CensusDrawer";
import CensusTable from "uicomponents/CensusTable";
import { CurrentEmployeesType } from "types";
import { DependentType, EmployeeType } from "uicomponents/CensusTable/types";
import {
  ParticipantTypeEnum,
  useDeleteDependentCensusMutation,
  useUpdateStepMutation,
} from "types/generated/operations";
import { mapBEStepstoMatchFE } from "pages/CensusAndCurrentInformation/utils/helper";
import {
  STEPS_WITH_COBRA,
  STEPS_WITHOUT_COBRA,
} from "pages/CensusAndCurrentInformation/utils/constants";
import Stepper from "uicomponents/Stepper";
import { errorMessageMap, getStepNumberFromURL } from "utils/helper";
import Error from "uicomponents/Error";
import Alert from "uicomponents/Alert";
interface ICurrentEmployeesProps {
  data: CurrentEmployeesType;
  refetchData: () => void;
}

interface IDrawerState {
  status: string;
  initialData?: DependentType | EmployeeType;
}

const CurrentEmployees = (props: ICurrentEmployeesProps): JSX.Element => {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageViewUserStep(5, "current empoloyee information");
  }, []);

  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [deleteDependentCensus, { loading: deleteDependentCensusLoading }] =
    useDeleteDependentCensusMutation();
  const [updateStep, { loading: updateStepLoading }] = useUpdateStepMutation();

  const [drawerState, setDrawerState] = useState<IDrawerState>({
    status: "closed",
    initialData: undefined,
  });

  const { data } = props;

  const [existingCensuses, setExistingCensuses] = useState(
    data?.employeeCensuses || []
  );

  useEffect(() => {
    if (data?.employeeCensuses) {
      setExistingCensuses(data.employeeCensuses);
    }
  }, [data]);

  const hasCobra = data?.healthInsuranceInformation?.cobraUsage;

  const { previousStep, nextStep, stepperNextStep } =
    useCensusAndCurrentInformationContext();

  const STEPS = hasCobra ? STEPS_WITH_COBRA : STEPS_WITHOUT_COBRA;

  const applicationStep = data?.applications?.[0]?.step;
  const currentStep = getStepNumberFromURL();
  const isInValidStep = applicationStep && currentStep > applicationStep;

  if (isInValidStep) {
    return <Error />;
  }

  const handleSaveAndContinue = async () => {
    try {
      if (data?.applications?.[0]?.step === currentStep) {
        await updateStep({
          variables: {
            uuid: data?.applications?.[0].uuid,
            step: data?.applications?.[0]?.step + 1,
          },
        });
        trackSaveAndContinueUserStep(currentStep, "Current Employees");
      }
      nextStep();
    } catch (error: any) {
      const errorMessage: string = error?.message;
      errorMessageMap[errorMessage] &&
        setErrorMessage(errorMessageMap[errorMessage]);
      trackFormError(error, errorMessage);
      setIsError(true);
      return;
    }
  };

  const handleDelete = async (uuid: string, participantType?: string) => {
    try {
      if (!uuid) {
        return true;
      }
      await deleteDependentCensus({
        variables: {
          uuid,
          participantType:
            (participantType
              ?.toUpperCase()
              .replace(/-/g, "_") as ParticipantTypeEnum) || "EMPLOYEE",
        },
      });
      props.refetchData();
      return true;
    } catch (error: any) {
      const errorMessage: string = error?.message;
      setErrorMessage(errorMessageMap[errorMessage] || "Please try again");
      trackFormError(error, errorMessage);
      setIsError(true);
      return false;
    }
  };

  const openDrawer = (initialData?: DependentType | EmployeeType) => () => {
    setDrawerState({
      status: "animating",
      initialData,
    });
    setTimeout(() => {
      setDrawerState({
        initialData,
        status: "open",
      });
    }, 10);
  };

  const closeDrawer = (refetch?: boolean) => {
    setDrawerState({
      ...drawerState,
      status: "animating",
    });
    setTimeout(() => {
      setDrawerState({
        status: "closed",
      });
    }, 500);
    if (refetch) props.refetchData();
  };

  return (
    <>
      <Modal
        isOpen={drawerState.status !== "closed"}
        onClose={() => closeDrawer()}
      >
        {drawerState.status !== "closed" && (
          <CensusDrawer
            isOpen={drawerState.status === "open"}
            initialData={drawerState.initialData}
            closeDrawer={closeDrawer}
          />
        )}
      </Modal>
      <div className={styles.censusContainer}>
        <Stepper
          steps={STEPS}
          currentStep={mapBEStepstoMatchFE(getStepNumberFromURL())}
          status={mapBEStepstoMatchFE(data?.applications?.[0].step || 0) || 8}
          onClick={(stepNumber) => stepperNextStep(stepNumber)}
        />
        <div className={styles.censusHeading}>
          <h3>{CURRENT_EMPLOYEES_DRAWER_FORM_TITLE}</h3>
          <div className={styles.description}>
            <ul className={styles.descriptionList}>
              <li>Full- and part-time employees (30-hours/week)</li>
              <li>And all dependents</li>
            </ul>
          </div>

          <CensusTable
            censusList={existingCensuses}
            handleBackClick={previousStep}
            handleDelete={handleDelete}
            offerHealthInsurance={
              !!props.data?.healthInsuranceInformation
                ?.currentlyOfferHealthInsurance
            }
            isLoading={deleteDependentCensusLoading || updateStepLoading}
            openDrawer={openDrawer}
            handleSaveAndContinue={handleSaveAndContinue}
          />
          {isError && <Alert message={errorMessage} />}
        </div>
      </div>
    </>
  );
};

export default CurrentEmployees;
